/**
 * Content Safety Utilities
 *
 * Lightweight client-side check for inappropriate content.
 * This is NOT a comprehensive filter - just catches obvious cases.
 * Designed to give kids a gentle nudge, not to block them.
 */

/**
 * Simple word list for obvious inappropriate content.
 * Keep this minimal - we don't want to be overly restrictive.
 * Focus on clear violations, not borderline cases.
 */
const FLAGGED_WORDS = [
  // Basic profanity (keeping list short and obvious)
  'damn',
  'hell',
  'crap',
  'stupid',
  'idiot',
  'dumb',
  'hate',
  'kill',
  'die',
  'dead',
  'blood',
  'gun',
  'shoot',
  'stab',
  'murder',
]

/**
 * Patterns that might indicate concerning content
 * These are checked separately from word list
 */
const CONCERNING_PATTERNS = [
  /i\s+hate\s+(myself|me)/i,
  /want\s+to\s+(die|hurt)/i,
  /kill\s+(myself|me)/i,
]

export interface ContentCheckResult {
  /** Whether the content passed the check (no flags) */
  isClean: boolean
  /** Words/phrases that were flagged */
  flaggedItems: string[]
  /** Whether the content might need adult attention */
  needsReview: boolean
  /** Kid-friendly message to show */
  message: string | null
}

/**
 * Check content for potentially inappropriate words/phrases.
 *
 * This is intentionally lenient - we want to catch obvious issues
 * while allowing creative expression. Violence in adventure stories,
 * characters who are mean, etc. are all normal parts of storytelling.
 *
 * @param content - The text to check
 * @returns Check result with flagged items and message
 */
export function checkContentSafety(content: string): ContentCheckResult {
  if (!content.trim()) {
    return {
      isClean: true,
      flaggedItems: [],
      needsReview: false,
      message: null,
    }
  }

  const normalizedContent = content.toLowerCase()
  const flaggedItems: string[] = []
  let needsReview = false

  // Check word list
  for (const word of FLAGGED_WORDS) {
    // Use word boundary to avoid false positives (e.g., "shell" matching "hell")
    const pattern = new RegExp(`\\b${word}\\b`, 'i')
    if (pattern.test(normalizedContent)) {
      flaggedItems.push(word)
    }
  }

  // Check concerning patterns (these warrant adult attention)
  for (const pattern of CONCERNING_PATTERNS) {
    if (pattern.test(normalizedContent)) {
      needsReview = true
      const match = normalizedContent.match(pattern)
      if (match) {
        flaggedItems.push(match[0])
      }
    }
  }

  // Only flag if there are multiple concerning words, or any concerning patterns
  // Single words like "hate" or "kill" are often fine in story context
  const isClean = flaggedItems.length < 2 && !needsReview

  let message: string | null = null
  if (!isClean) {
    if (needsReview) {
      message =
        "Your story has some words that might be concerning. If you're feeling sad or upset, please talk to a trusted adult."
    } else {
      message =
        "Some words in your story might not be appropriate. Would you like to review your story before submitting?"
    }
  }

  return {
    isClean,
    flaggedItems,
    needsReview,
    message,
  }
}

/**
 * Get a kid-friendly explanation for why content was flagged.
 * Use this when showing the warning to make it educational.
 */
export function getContentWarningExplanation(): string {
  return "Great stories can include conflict and challenges, but some words might hurt others' feelings or aren't appropriate for young writers. Try finding different words to express the same ideas!"
}
